import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Container,
  Card,
  CardContent,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ManagetNav from '../../components/ManagerNav/ManagerNav';
import ProductApi from '../../api/ProductApi';
import { ProductDto, PatchProductDto } from '../../interfaces/ProductInterfaces';
import { licenseTypeCodeToLabel, PRODUCT_LICENSE_TYPE_OPTIONS, ProductLicenseTypeCode } from '../../utils/productLicenseType';
import './ManagerProductView.scss';

interface ProductFormData {
  Product_Name: string;
  Product_LicenseType: ProductLicenseTypeCode | '';
  Product_Desc: string;
  Product_Cost: number;
}

const ManagerProductView: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const productGuid = searchParams.get('Product_guid');

  const [product, setProduct] = useState<ProductDto | null>(null);
  const [formData, setFormData] = useState<ProductFormData>({
    Product_Name: '',
    Product_LicenseType: '',
    Product_Desc: '',
    Product_Cost: 0
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  // Fetch product details
  const fetchProduct = async () => {
    if (!productGuid) return;
    
    setLoading(true);
    setError(null);

    try {
      const productData = await ProductApi.getProduct(productGuid);
      setProduct(productData);
      setFormData({
        Product_Name: productData.Product_Name,
        Product_LicenseType: productData.Product_LicenseType ?? '',
        Product_Desc: productData.Product_Desc,
        Product_Cost: productData.Product_Cost
      });
      
    } catch (err) {
      console.error('Error fetching product:', err);
      setError(err instanceof Error ? err.message : 'Failed to load product');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (productGuid) {
      fetchProduct();
    }
  }, [productGuid]);

  // Handle form changes
  const handleFormChange = (field: keyof ProductFormData, value: string | number) => {
    setFormData({ ...formData, [field]: value });
    // Clear success message when editing
    if (success) setSuccess(null);
  };

  // Handle save
  const handleSave = async () => {
    if (!productGuid) return;
    
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const updateData: PatchProductDto = {};
      
      if (formData.Product_Name !== product?.Product_Name) {
        updateData.Product_Name = formData.Product_Name;
      }
      if (formData.Product_LicenseType !== (product?.Product_LicenseType ?? '')) {
        if (!formData.Product_LicenseType) {
          setError('License type is required');
          setSaving(false);
          return;
        }
        updateData.Product_LicenseType = formData.Product_LicenseType;
      }
      if (formData.Product_Desc !== product?.Product_Desc) {
        updateData.Product_Desc = formData.Product_Desc;
      }
      if (formData.Product_Cost !== product?.Product_Cost) {
        updateData.Product_Cost = formData.Product_Cost;
      }

      if (Object.keys(updateData).length === 0) {
        setSuccess('No changes to save');
        setIsEditing(false);
        return;
      }

      const updatedProduct = await ProductApi.updateProduct(productGuid, updateData);
      setProduct(updatedProduct);
      setSuccess('Product updated successfully');
      setIsEditing(false);
      
    } catch (err) {
      console.error('Error updating product:', err);
      setError(err instanceof Error ? err.message : 'Failed to update product');
    } finally {
      setSaving(false);
    }
  };

  // Handle edit toggle
  const handleEdit = () => {
    setIsEditing(!isEditing);
    if (isEditing) {
      // Reset form data if cancelling edit
      if (product) {
        setFormData({
          Product_Name: product.Product_Name,
          Product_LicenseType: product.Product_LicenseType ?? '',
          Product_Desc: product.Product_Desc,
          Product_Cost: product.Product_Cost
        });
      }
    }
    setSuccess(null);
    setError(null);
  };

  // Helper functions
  const formatCurrency = (cost: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(cost / 100);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Not provided';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  if (!productGuid) {
    return (
      <>
        <ManagetNav />
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
          <Alert severity="error">
            Product GUID is required
          </Alert>
        </Container>
      </>
    );
  }

  return (
    <>
      <ManagetNav />
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/products')}
            sx={{ mr: 2 }}
          >
            Back to Products
          </Button>
          
          <Typography variant="h4" component="h1" sx={{ flexGrow: 1 }}>
            Product Details
          </Typography>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={handleEdit}
              disabled={loading}
              color={isEditing ? "error" : "primary"}
            >
              {isEditing ? 'Cancel Edit' : 'Edit Product'}
            </Button>
            
            {isEditing && (
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                onClick={handleSave}
                disabled={saving || !formData.Product_Name.trim() || !formData.Product_LicenseType}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            )}
          </Box>
        </Box>

        {product && !product.Product_LicenseType && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This product is missing a license type. Set one before it can be matched to tickets.
          </Alert>
        )}

        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Success Display */}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {/* Main Content */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : product ? (
          <Grid container spacing={3}>
            {/* Product Information */}
            <Grid size={{ xs: 12, md: 8 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Product Information
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <TextField
                      label="Product Name"
                      value={formData.Product_Name}
                      onChange={(e) => handleFormChange('Product_Name', e.target.value)}
                      disabled={!isEditing}
                      fullWidth
                      required
                      variant={isEditing ? "outlined" : "standard"}
                      InputProps={{
                        readOnly: !isEditing,
                      }}
                    />

                    {isEditing ? (
                      <FormControl fullWidth required>
                        <InputLabel id="product-license-type-label">License Type</InputLabel>
                        <Select
                          labelId="product-license-type-label"
                          value={formData.Product_LicenseType}
                          label="License Type"
                          onChange={(e) => handleFormChange('Product_LicenseType', e.target.value as ProductLicenseTypeCode)}
                        >
                          {PRODUCT_LICENSE_TYPE_OPTIONS.map((option) => (
                            <MenuItem key={option.code} value={option.code}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <Box>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          License Type
                        </Typography>
                        {product.Product_LicenseType ? (
                          <Chip
                            label={licenseTypeCodeToLabel(product.Product_LicenseType)}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        ) : (
                          <Chip label="Not set" size="small" color="warning" variant="outlined" />
                        )}
                      </Box>
                    )}
                    
                    <TextField
                      label="Description"
                      value={formData.Product_Desc}
                      onChange={(e) => handleFormChange('Product_Desc', e.target.value)}
                      disabled={!isEditing}
                      fullWidth
                      multiline
                      rows={4}
                      variant={isEditing ? "outlined" : "standard"}
                      InputProps={{
                        readOnly: !isEditing,
                      }}
                      placeholder="No description provided"
                    />
                    
                    <TextField
                      label="Cost (in cents)"
                      type="number"
                      value={formData.Product_Cost}
                      onChange={(e) => handleFormChange('Product_Cost', parseInt(e.target.value) || 0)}
                      disabled={!isEditing}
                      fullWidth
                      required
                      variant={isEditing ? "outlined" : "standard"}
                      InputProps={{
                        readOnly: !isEditing,
                      }}
                      inputProps={{ min: 0 }}
                      helperText={`Display Price: ${formatCurrency(formData.Product_Cost)}`}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Product Metadata */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Product Metadata
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box>
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        Product GUID
                      </Typography>
                      <Typography variant="body1" sx={{ wordBreak: 'break-all', fontSize: '0.875rem' }}>
                        {product.Product_guid}
                      </Typography>
                    </Box>
                    
                    <Divider />
                    
                    <Box>
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        Created At
                      </Typography>
                      <Typography variant="body1">
                        {formatDate(product.Product_CreatedAt.toString())}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        Last Updated
                      </Typography>
                      <Typography variant="body1">
                        {formatDate(product.Product_UpdatedAt.toString())}
                      </Typography>
                    </Box>
                    
                    <Divider />
                    
                    <Box>
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        Current Price
                      </Typography>
                      <Typography variant="h5" color="primary" fontWeight="bold">
                        {formatCurrency(product.Product_Cost)}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        ) : (
          <Alert severity="error">
            Product not found
          </Alert>
        )}

      </Container>
    </>
  );
};

export default ManagerProductView;